import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { MAX_SUMMARY_LINES, isIndexedSchedule, ownerZoneNow, scheduleZoneNow } from '@/components/arrayColumns/scheduleCell';
import { withPendingScheduleWrites } from '@/composables/api/scheduleItems';
import { scheduleNow } from '@/composables/useScheduleClock';
import {
    asScheduleRows,
    orderBySchedulesSort,
    positiveZoneId,
    sortCardSchedules,
    toEntries,
    type ScheduleEntry,
} from './scheduleGroups';
import { injectCardSchedules, injectTableScheduleSort, injectTableSchedules } from './cardSchedulesContext';

/**
 * The ids of the schedules that passed the search's schedule rules, read from a
 * custom list search row. The backend sends `matched_schedule_ids` on every
 * search row. It is null when the filter had no schedule rules, which means
 * every schedule on the list passed. A row that did not come from a search,
 * such as a record the inspector fetched, has no such field and reads as null.
 */
export function matchedScheduleIdsOf(row: Record<string, unknown>): ReadonlySet<number> | null {
    const matched = row.matched_schedule_ids;
    if (!Array.isArray(matched)) return null;
    return new Set(matched.map(Number).filter((id) => Number.isInteger(id) && id > 0));
}

/**
 * The schedules a custom list's search card and table cell draw, in the order
 * they draw them.
 *
 * 1. The row's schedules, with this browser's pending writes applied. A schedule
 *    this browser edited shows its new values, and one it deleted is gone.
 * 2. Only schedules the index has written in full. An old `{id, display_string}`
 *    item has no type or times to draw.
 * 3. When the search applied schedule rules, only the schedules whose ids are in
 *    `matched_schedule_ids`. For example, a filter for schedules starting Nov 1–7
 *    on a list of 12 schedules keeps the 2 that start then.
 * 4. Sorted with {@link sortCardSchedules}: running now, then upcoming, then expired.
 *
 * A schedule this browser created since the search ran is not in the index yet.
 * It shows only when the search applied no schedule rules. The browser cannot
 * tell whether a new schedule would pass the rules, so it leaves it out rather
 * than guess. The step in 3 does that on its own, because a new schedule's id is
 * never in `matched_schedule_ids`.
 *
 * The backend also reports `schedule_rules_applied` in the response's `meta`.
 * The row does not need it. `PassingSchedules::fromHit` returns null exactly when
 * the rules were not applied, so each row's own `matched_schedule_ids` already
 * says which case it is in.
 *
 * The card, the table cell and the search page's list of schedule ids all read
 * this one function, so they never disagree about which schedules a row shows.
 */
export function cardSchedulesFor(row: Record<string, unknown>, instant: Date = scheduleNow()): ScheduleEntry[] {
    const matchedIds = matchedScheduleIdsOf(row);
    const schedules = asScheduleRows(withPendingScheduleWrites(row.id, row.location_zone_custom_playlist_schedules))
        .filter(isIndexedSchedule)
        .filter((schedule) => matchedIds === null || matchedIds.has(Number(schedule.id)));
    if (schedules.length === 0) return [];
    const now = ownerZoneNow(row, instant);
    const entries = toEntries(schedules, now, undefined, (schedule) => scheduleZoneNow(schedule, row, instant));
    return sortCardSchedules(entries, positiveZoneId(row.location_zone_id));
}

/** The key a page's map of card schedules uses for a row. */
export function cardSchedulesKeyOf(row: Record<string, unknown>): string {
    return String(row.id);
}

/**
 * {@link cardSchedulesFor} as a computed, for a component that draws one row.
 *
 * On the search page it reads the entries the page already worked out for the row. That includes
 * the split view's inspector, which sits inside the search page. Anywhere else it works them out
 * itself.
 */
export function useCardSchedules(row: MaybeRefOrGetter<Record<string, unknown>>) {
    const pageCardSchedules = injectCardSchedules();
    return computed(() => {
        const shownRow = toValue(row);
        const fromPage = pageCardSchedules ? toValue(pageCardSchedules).get(cardSchedulesKeyOf(shownRow)) : undefined;
        return fromPage ?? cardSchedulesFor(shownRow);
    });
}

/**
 * A row's table schedule lines: its card entries, put in the order of the sort the rows were
 * searched with (see orderBySchedulesSort), and cut to the first MAX_SUMMARY_LINES.
 */
export function tableSchedulesFor(
    entries: ScheduleEntry[],
    searchedSort: Parameters<typeof orderBySchedulesSort>[1],
): ScheduleEntry[] {
    return orderBySchedulesSort(entries, searchedSort).slice(0, MAX_SUMMARY_LINES);
}

/**
 * The schedule lines a table row draws, in every one of its schedule cells: the "schedules"
 * cell and each schedule column's cell. See tableSchedulesFor.
 *
 * Every cell cuts them before it draws them, so line 2 of every schedule cell in a row is the
 * same schedule. Inside the search page's table they come from the map TableScheduleSortScope
 * works out once per row, so a row is not sorted once per cell. Anywhere else, or for a row
 * missing from the map, the cell works them out itself. Only the table cells read this. A card
 * keeps its own order whatever the sort.
 */
export function useTableSchedules(row: MaybeRefOrGetter<Record<string, unknown>>) {
    const tableSchedules = injectTableSchedules();
    const entries = useCardSchedules(row);
    const searchedSort = injectTableScheduleSort();
    return computed(() => {
        const fromTable = tableSchedules ? toValue(tableSchedules).get(cardSchedulesKeyOf(toValue(row))) : undefined;
        return fromTable ?? tableSchedulesFor(entries.value, searchedSort ? toValue(searchedSort) : null);
    });
}
